// ---------------------------------------------------------------------------
// Helpers de EXIBIÇÃO do módulo Movements (estoque) — compartilhados pela
// listagem, pelo detalhe e pelo editor. Promovidos das páginas em 2026-09-12,
// quando o redesenho passou a precisar do mesmo rótulo/data nas três telas
// (migrate-on-touch: extrair, não copiar).
//
// ⚠️ O módulo convive com DOIS formatos de data no MESMO payload (doc de
// arquitetura §4): o `GET /movements/:id` e o `POST /movement-search` legados
// (Geração 2) devolvem `created_at` já formatado em pt-BR pelo backend
// ("11/08/2026, 11:38:49"), enquanto `cancelled_at` (handoff 0095) e as
// respostas do núcleo de escrita G3 (0093) vêm em ISO 8601 UTC.
//
// Reparsear a string pt-BR com `new Date` cai no parser leniente do V8, que lê
// MM/DD: `11/08/2026` virava 8 de novembro e era reimpresso trocado — só dia
// >= 13 produzia mês inválido, NaN e fallback (bug real, corrigido em
// 2026-08-27). Por isso TODA leitura de data do módulo passa por aqui, e o
// pt-BR é interpretado pelo regex, nunca pelo construtor de Date.
// ---------------------------------------------------------------------------

// dd/mm/aaaa, com hora opcional (o separador pode ser vírgula ou só espaço).
const BR_DATETIME_RE =
   /^(\d{2})\/(\d{2})\/(\d{4})(?:,?\s+(\d{1,2}):(\d{2})(?::(\d{2}))?)?/;

// Quebra o valor nas duas linhas que a tabela usa (data em cima, hora abaixo).
// Devolve strings vazias quando não há valor — a célula decide o "—".
export function movementDateParts(value) {
   if (!value) return { date: '', time: '' };

   const raw = String(value);
   const br = raw.match(BR_DATETIME_RE);
   if (br) {
      const [, day, month, year, hour, minute] = br;
      return {
         date: `${day}/${month}/${year}`,
         time: hour ? `${hour.padStart(2, '0')}:${minute}` : '',
      };
   }

   const parsed = new Date(raw);
   if (Number.isNaN(parsed.getTime())) return { date: raw, time: '' };

   return {
      date: parsed.toLocaleDateString('pt-BR'),
      time: parsed.toLocaleTimeString('pt-BR', {
         hour: '2-digit',
         minute: '2-digit',
      }),
   };
}

// Data + hora em uma linha (campos do detalhe). Sem `timeZone` explícito, como
// decidido em 2026-08-27: a operação é toda -03:00 e o valor pt-BR do backend
// já vem convertido — reconverter seria errado duas vezes.
export function formatMovementDateTime(value) {
   if (!value) return '';
   const raw = String(value);
   if (BR_DATETIME_RE.test(raw)) return raw;

   const parsed = new Date(raw);
   if (Number.isNaN(parsed.getTime())) return raw;
   return parsed.toLocaleString('pt-BR');
}

// Valor comparável para ordenação CRONOLÓGICA. A ordenação da listagem é
// client-side e antes comparava a string pt-BR lexicograficamente — o que
// ordena por dia do mês ("02/09" antes de "11/08"). Invisível quando o filtro
// é de um único dia, errado em qualquer intervalo maior.
export function movementDateSortValue(value) {
   if (!value) return 0;

   const raw = String(value);
   const br = raw.match(BR_DATETIME_RE);
   if (br) {
      const [, day, month, year, hour = '0', minute = '0', second = '0'] = br;
      return new Date(
         Number(year),
         Number(month) - 1,
         Number(day),
         Number(hour),
         Number(minute),
         Number(second)
      ).getTime();
   }

   const parsed = new Date(raw);
   return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
}

// "3 itens · 9 un." — agregados de itens que a listagem passou a receber no
// header do movimento (handoff 0109, 2026-09-12): `items_count` conta LINHAS
// de `item_movements` (produto repetido em duas linhas conta 2) e
// `units_total` é a soma de `prod_qtd`. Ambos são do MOVIMENTO INTEIRO, mesmo
// quando a busca filtra por produto — o backend calcula em query separada,
// não sobre o join filtrado.
//
// Pelo contrato os dois são inteiros e nunca nulos. O retorno vazio existe
// para a janela em que o backend do 0109 ainda não subiu: melhor a célula
// vazia do que "0 itens · 0 un." mentindo sobre um movimento que tem itens.
export function formatItemsSummary(itemsCount, unitsTotal) {
   if (itemsCount === null || itemsCount === undefined) return '';

   const count = Number(itemsCount) || 0;
   const units = Number(unitsTotal) || 0;
   return `${count} ${count === 1 ? 'item' : 'itens'} · ${units} un.`;
}

export const MOVE_TYPE_LABEL = { in: 'Entrada', out: 'Saída' };

export function moveTypeLabel(moveType) {
   return MOVE_TYPE_LABEL[moveType] ?? '';
}

// Pílula de tipo: entrada some estoque (verde), saída subtrai (azul) — mesma
// semântica dos badges de transferência.
export function moveTypeVariant(moveType) {
   if (moveType === 'in') return 'green';
   if (moveType === 'out') return 'blue';
   return 'neutral';
}

function normalizeFlag(flag) {
   return String(flag ?? '')
      .trim()
      .toLowerCase();
}

// `movement_flag` é ENUM no backend desde 2026-08-16 (`confirmed|deleted|
// reversion`), mas o alias `excluded` de payloads antigos segue aceito, como
// na listagem original. `reversion` passou a ter rótulo em PT (antes aparecia
// cru, "REVERSION", em cinza).
export function movementFlagLabel(flag) {
   const f = normalizeFlag(flag);
   if (f === 'confirmed') return 'CONFIRMADO';
   if (f === 'deleted' || f === 'excluded') return 'EXCLUÍDO';
   if (f === 'reversion') return 'REVERSÃO';
   return f ? f.toUpperCase() : '';
}

export function movementFlagVariant(flag) {
   const f = normalizeFlag(flag);
   if (f === 'confirmed') return 'green';
   if (f === 'deleted' || f === 'excluded') return 'red';
   return 'neutral';
}

// Nomes que o backend resolve na listagem/detalhe com cascatas diferentes
// (`pdv_name` flat na listagem, `Pdv.pdv_name` no include). Uma função só
// evita a divergência listagem × detalhe registrada no débito #6 do doc.
export function movementPdvLabel(movement) {
   const name =
      movement?.pdv_name ?? movement?.Pdv?.pdv_name ?? movement?.pdv_id ?? '';
   const id = movement?.pdv_id;
   if (!id) return String(name || '');
   return String(name) === String(id) ? `PDV ${id}` : `${id} - ${name}`;
}

export function movementUserLabel(movement) {
   const name =
      movement?.user_name ??
      movement?.User?.nome ??
      movement?.User?.name ??
      movement?.user_id ??
      '';
   const id = movement?.user_id;
   if (!id) return String(name || '');
   return String(name) === String(id) ? `Usuário ${id}` : `${id} - ${name}`;
}
